import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { generateLeaderboardUrl } from "../Utilities/shareUtils";

const PublicTeamPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [tieBreakerScore, setTieBreakerScore] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const fetchTeamData = useCallback(async () => {
    try {
      const response = await fetch(`/api/team/getTeamByToken?token=${token}`);
      if (!response.ok) {
        throw new Error(response.status === 404 ? "Team not found. This link may be invalid." : "Failed to load team data.");
      }
      const result = await response.json();
      setData(result);

      // Initialize selected players from line items
      const maxPlayers = result.pool.maxPlayers || 0;
      const players = new Array(maxPlayers).fill(null).map((_, index) => {
        const lineItem = result.lineItems[index];
        if (lineItem?.player) {
          return {
            id: lineItem.player.id,
            name: lineItem.player.player_name,
            worldRanking: lineItem.player.world_ranking_position,
            displayOrder: lineItem.display_order || index + 1,
          };
        }
        return { id: "", name: "", worldRanking: 0, displayOrder: index + 1 };
      });
      setSelectedPlayers(players);

      if (result.team.tieBreakerScore !== null && result.team.tieBreakerScore !== undefined) {
        setTieBreakerScore(result.team.tieBreakerScore.toString());
      }

      // Auto-enter edit mode if team is editable and has empty slots
      if (result.isEditable && players.some(p => !p.id)) {
        setIsEditing(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  const handlePlayerSelect = (index, e) => {
    const option = e.target.options[e.target.selectedIndex];
    if (!option) return;

    const playerId = Number(option.getAttribute("data-player-id"));
    const playerName = option.getAttribute("data-player-name");
    const worldRanking = Number(option.getAttribute("data-world-ranking"));

    if (playerId && playerName) {
      const newPlayers = [...selectedPlayers];
      newPlayers[index] = {
        id: playerId,
        name: playerName,
        worldRanking,
        displayOrder: index + 1,
      };
      setSelectedPlayers(newPlayers);
    }
  };

  const isPlayerSelected = (playerId, currentIndex) => {
    return selectedPlayers.some((p, i) => i !== currentIndex && p?.id === playerId);
  };

  const handleSave = async () => {
    // Validate all slots filled
    const emptySlots = selectedPlayers.filter(p => !p.id);
    if (emptySlots.length > 0) {
      toast.error(`Please select all ${data.pool.maxPlayers} players`, { theme: "dark" });
      return;
    }

    // Validate tie breaker
    if (data.pool.tieBreaker === "Winning Score") {
      if (!tieBreakerScore || tieBreakerScore.trim() === "") {
        toast.error("Please enter a tie breaker score", { theme: "dark" });
        return;
      }
    }

    setIsSaving(true);
    try {
      const response = await fetch("/api/team/saveTeamByToken", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          selectedPlayers: selectedPlayers.map(p => ({
            id: p.id,
            worldRanking: p.worldRanking,
            displayOrder: p.displayOrder,
          })),
          tieBreakerScore: data.pool.tieBreaker === "Winning Score" ? parseInt(tieBreakerScore) : undefined,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Failed to save team");
      }

      toast.success("Team saved!", { theme: "dark" });
      setIsEditing(false);
      fetchTeamData();
    } catch (err) {
      toast.error(err.message, { theme: "dark" });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-500">Loading your team...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Team</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const { team, pool, playerTournaments, isEditable } = data;
  const showTieBreaker = pool.tieBreaker === "Winning Score";
  const leaderboardUrl = pool.leaderboardToken ? generateLeaderboardUrl(pool.leaderboardToken) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer position="top-center" theme="dark" />

      {/* Header */}
      <div className="bg-primary text-white">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <p className="text-green-200 text-sm font-medium uppercase tracking-wider">GolfPools</p>
          <h1 className="text-2xl font-bold mt-1">{pool.name}</h1>
          <p className="text-green-200 mt-1">Team: {team.playerName || "Your Team"}</p>
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-white border-b">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="text-gray-500">Status: <span className="font-medium text-gray-900">{pool.tournamentStatus || "Not Started"}</span></span>
            {pool.currentRound && <span className="text-gray-500">Round: <span className="font-medium text-gray-900">{pool.currentRound}</span></span>}
          </div>
          {leaderboardUrl && (
            <a href={leaderboardUrl} className="text-primary font-medium hover:underline">
              View Leaderboard
            </a>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Edit Toggle */}
        {isEditable && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="w-full mb-4 bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
          >
            Edit Team
          </button>
        )}

        {/* Player Cards */}
        <div className="space-y-3">
          {selectedPlayers.map((player, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Player {index + 1}</span>
                  {!isEditing && player.worldRanking > 0 && (
                    <span className="text-xs text-gray-500">WR #{player.worldRanking}</span>
                  )}
                </div>

                {isEditing ? (
                  <select
                    className="w-full mt-1 px-3 py-2.5 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 bg-white"
                    onChange={(e) => handlePlayerSelect(index, e)}
                    value={player.id || ""}
                  >
                    <option value="">Select a Player</option>
                    {[...playerTournaments].sort((a, b) => a.player.player_name.localeCompare(b.player.player_name)).map((pt) => {
                      const isSelected = player.id === pt.player.id;
                      return (
                        <option
                          key={pt.id}
                          value={pt.player.id}
                          data-player-id={pt.player.id}
                          data-player-name={pt.player.player_name}
                          data-world-ranking={pt.player.world_ranking_position}
                          disabled={!isSelected && isPlayerSelected(pt.player.id, index)}
                        >
                          {pt.player.player_name} (#{pt.player.world_ranking_position})
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <p className={`text-lg font-semibold ${player.name ? "text-gray-900" : "text-red-500"}`}>
                    {player.name || "Not Selected"}
                  </p>
                )}

                {/* Score info (when not editing and tournament active) */}
                {!isEditing && player.id && data.lineItems[index] && (
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    {data.lineItems[index].tournament_position && (
                      <span className="text-gray-500">
                        Pos: <span className="font-medium text-gray-900">{data.lineItems[index].tournament_position}</span>
                      </span>
                    )}
                    {data.lineItems[index].total_score_to_par && data.lineItems[index].total_score_to_par !== "--" && (
                      <span className="text-gray-500">
                        Score: <span className={`font-medium ${
                          parseInt(data.lineItems[index].total_score_to_par) < 0 ? "text-red-600" :
                          parseInt(data.lineItems[index].total_score_to_par) > 0 ? "text-blue-600" : "text-gray-900"
                        }`}>{data.lineItems[index].total_score_to_par}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Tie Breaker */}
        {showTieBreaker && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-3">
            <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Tie Breaker Score</label>
            {isEditing ? (
              <div className="mt-1">
                <input
                  type="number"
                  min="0"
                  value={tieBreakerScore}
                  onChange={(e) => setTieBreakerScore(e.target.value)}
                  placeholder="Enter winning score (e.g. 276)"
                  className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
                <p className="text-xs text-gray-400 mt-1">Total strokes, not relative to par (e.g. 276 not -12)</p>
              </div>
            ) : (
              <p className="text-lg font-semibold text-gray-900 mt-1">{tieBreakerScore || "--"}</p>
            )}
          </div>
        )}

        {/* Save / Cancel Buttons */}
        {isEditing && (
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => { setIsEditing(false); fetchTeamData(); }}
              className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-all"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save Team"}
            </button>
          </div>
        )}

        {/* Not Editable Notice */}
        {!isEditable && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <p className="text-amber-800 text-sm font-medium">
              The tournament has started. Team editing is closed.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicTeamPage;
